import { Alert, AnimatedContentLoader, Button, ReferralShareLink } from '@components'
import { SettingsStatGrid } from '@pages/SettingsPage/components/SettingsStatGrid'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { useReferralMyLink } from '@query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { resolveReferralShareLink } from '@utils/referralLink'
import { ReferralRewardsSection } from './ReferralRewardsSection'

export const ReferralSettings: React.FC = () => {
  const [t] = useTranslation()
  const { data, isLoading, isError, refetch, isFetching } = useReferralMyLink()

  return (
    <SettingsSubPageLayout title={t('settings.referral.mainTitle')} contentPadding="md">
      <Sections>
        <ReferralSection>
          <AnimatedContentLoader isLoading={isLoading}>
            {isError ? (
              <ErrorState>
                <Alert type="error" description={t('settings.referral.linkLoadError')} />
                <Button showSpinner={isFetching} onClick={() => refetch()}>
                  {t('general.retry')}
                </Button>
              </ErrorState>
            ) : (
              <ShareSectionContent>
                <Alert marginBottom={0} description={t('settings.referral.description')} />

                <FieldBlock>
                  <FieldLabel>{t('settings.referral.linkLabel')}</FieldLabel>
                  {data?.link ? (
                    <ReferralShareLink
                      url={resolveReferralShareLink({ code: data.code, link: data.link })}
                      shareTitle={t('settings.referral.shareTitle')}
                      shareMessage={t('settings.referral.shareMessage')}
                    />
                  ) : (
                    <FieldPlaceholder>-</FieldPlaceholder>
                  )}
                </FieldBlock>

                <SettingsStatGrid
                  items={[
                    {
                      value: data?.total_referrals ?? 0,
                      label: t('settings.referral.stats.invited')
                    },
                    {
                      value: data?.available_count ?? 0,
                      label: t('settings.referral.stats.available')
                    },
                    {
                      value: data?.redeemed_count ?? 0,
                      label: t('settings.referral.stats.redeemed')
                    }
                  ]}
                />
              </ShareSectionContent>
            )}
          </AnimatedContentLoader>
        </ReferralSection>

        <ReferralSection>
          <SectionTitle>{t('settings.referral.redeem.sectionTitle')}</SectionTitle>
          <ReferralRewardsSection />
        </ReferralSection>
      </Sections>
    </SettingsSubPageLayout>
  )
}

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
`

const ReferralSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const ShareSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  min-width: 0;
`

const FieldLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const FieldPlaceholder = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
